import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// localStorage keys
const STORAGE_KEY_MEMBERSHIP = 'socrates-current-membership';
const STORAGE_KEY_SUPER_ADMIN = 'socrates-super-admin-membership';

export interface LeagueMembership {
  id: string;
  league_id: string;
  display_name: string;
  team_name: string | null;
  avatar_url: string | null;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'invited' | 'suspended' | 'left';
  welcomed_at: string | null;
  league: {
    id: string;
    name: string;
    slug: string;
    season: string | null;
  };
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isSuperAdmin: boolean;
  checkingSuperAdmin: boolean;
  memberships: LeagueMembership[];
  membershipsError: Error | null;
  membershipsErrorStatus: number | null;
  loadingMemberships: boolean;
  superAdminErrorStatus: number | null;
  currentMembership: LeagueMembership | null;
  setCurrentMembership: (membership: LeagueMembership | null) => void;
  refreshMemberships: () => Promise<LeagueMembership[]>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string, redirectTo?: string) => Promise<{ error: Error | null }>;
  signInWithPasskey: (tokenHash: string, email: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name: string, redirectTo?: string) => Promise<{ error: Error | null; session: Session | null }>;
  signUpWithOtp: (email: string, name: string, redirectTo?: string) => Promise<{ error: Error | null }>;
  verifyOtp: (email: string, token: string, type?: 'email' | 'signup') => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingSuperAdmin, setCheckingSuperAdmin] = useState(false);
  const [memberships, setMemberships] = useState<LeagueMembership[]>([]);
  const [membershipsError, setMembershipsError] = useState<Error | null>(null);
  const [membershipsErrorStatus, setMembershipsErrorStatus] = useState<number | null>(null);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [currentMembership, setCurrentMembershipState] = useState<LeagueMembership | null>(null);
  const [superAdminErrorStatus, setSuperAdminErrorStatus] = useState<number | null>(null);

  // Always-current memberships ref (avoids stale closures in loadMemberships early exits)
  const membershipsRef = useRef(memberships);
  membershipsRef.current = memberships;

  // Track if we're currently checking to prevent duplicate calls (use ref to avoid re-renders)
  const checkingSuperAdminRef = useRef(false);
  // Cache the last checked user ID to avoid re-checking the same user
  const lastCheckedUserIdRef = useRef<string | null>(null);
  // Track consecutive failures to prevent infinite retry loops
  const failureCountRef = useRef(0);
  const lastFailureTimeRef = useRef<number>(0);

  // Helper to extract HTTP status code from Supabase errors
  const extractStatusCode = (error: unknown): number | null => {
    const err = error as Record<string, unknown> | null;
    // Check direct status property (edge function errors)
    if (typeof err?.status === 'number') {
      return err.status;
    }

    // Check code property (might be numeric for some errors)
    if (typeof err?.code === 'number') {
      return err.code;
    }

    // Check error message for status codes (fallback)
    if (typeof err?.message === 'string') {
      const statusMatch = err.message.match(/\b(40[0-9]|50[0-9])\b/);
      if (statusMatch) {
        return parseInt(statusMatch[1], 10);
      }

      // Check for service unavailable keywords
      if (err.message.toLowerCase().includes('service unavailable') ||
          err.message.toLowerCase().includes('503')) {
        return 503;
      }
    }

    return null;
  };

  // Check super admin status via Edge Function (keeps emails secret)
  const checkSuperAdminStatus = useCallback(async (userId?: string, userEmail?: string): Promise<boolean> => {
    // Use provided parameters or fall back to context state
    const currentUserId = userId || user?.id;
    const currentUserEmail = userEmail || user?.email;
    const currentSession = session;
    
    console.log('🔍 checkSuperAdminStatus called', { 
      hasUser: !!user, 
      hasSession: !!currentSession,
      userId: currentUserId,
      userEmail: currentUserEmail,
      cachedUserId: lastCheckedUserIdRef.current,
      failureCount: failureCountRef.current,
      usingParams: !!(userId || userEmail)
    });
    
    // If we have userId and userEmail from parameters, we can proceed even without context state
    if (!currentUserId || !currentUserEmail) {
      // Try to get session directly from Supabase if context state isn't ready
      if (!currentSession) {
        const { data: { session: directSession } } = await supabase.auth.getSession();
        if (directSession?.user) {
          const directUserId = directSession.user.id;
          const directUserEmail = directSession.user.email;
          
          if (directUserId && directUserEmail) {
            // Use the direct session data
            console.log('📡 Using direct session data:', { userId: directUserId, email: directUserEmail });
            return await checkSuperAdminStatus(directUserId, directUserEmail);
          }
        }
      }
      
      console.log('❌ No user or session available');
      setIsSuperAdmin(false);
      lastCheckedUserIdRef.current = null;
      failureCountRef.current = 0;
      return false;
    }

    // If we already checked this user successfully, return cached result
    if (lastCheckedUserIdRef.current === currentUserId && failureCountRef.current === 0) {
      console.log('✅ Using cached super admin result:', isSuperAdmin, 'for user:', currentUserEmail);
      return isSuperAdmin;
    }

    console.log('🚀 Proceeding with super admin check for:', currentUserEmail);

    // Circuit breaker: if we've failed 3+ times in the last 30 seconds, stop trying
    const now = Date.now();
    const timeSinceLastFailure = now - lastFailureTimeRef.current;
    if (failureCountRef.current >= 3 && timeSinceLastFailure < 30000) {
      // Reset after 30 seconds
      if (timeSinceLastFailure >= 30000) {
        failureCountRef.current = 0;
      } else {
        return isSuperAdmin; // Return cached value
      }
    }

    // Prevent duplicate simultaneous calls
    if (checkingSuperAdminRef.current) {
      return isSuperAdmin;
    }

    checkingSuperAdminRef.current = true;
    setCheckingSuperAdmin(true);
    setSuperAdminErrorStatus(null); // Clear previous error status
    try {
      const { data, error } = await supabase.functions.invoke('check-super-admin');
      
      if (error) {
        failureCountRef.current++;
        lastFailureTimeRef.current = Date.now();
        console.error('Error checking super admin status:', error);
        // Extract status code from error
        const status = extractStatusCode(error);
        setSuperAdminErrorStatus(status);
        setIsSuperAdmin(false);
        // Don't cache on failure - allow retry after delay
        return false;
      }
      
      // Success - clear error status
      setSuperAdminErrorStatus(null);

      // Success - reset failure count and cache (even if result is false)
      failureCountRef.current = 0;
      const isAdmin = data?.isSuperAdmin ?? false;
      console.log('✅ Super admin check result:', isAdmin, 'for', currentUserEmail);
      setIsSuperAdmin(isAdmin);
      lastCheckedUserIdRef.current = currentUserId; // Cache that we checked this user (even if false)
      return isAdmin;
    } catch (error) {
      failureCountRef.current++;
      lastFailureTimeRef.current = Date.now();
      console.error('Error checking super admin status:', error);
      // Extract status code from error
      const status = extractStatusCode(error);
      setSuperAdminErrorStatus(status);
      setIsSuperAdmin(false);
      return false;
    } finally {
      checkingSuperAdminRef.current = false;
      setCheckingSuperAdmin(false);
    }
  }, [user, session, isSuperAdmin]);

  // Persist membership to localStorage when it changes
  const setCurrentMembership = useCallback((membership: LeagueMembership | null) => {
    setCurrentMembershipState(membership);
    
    if (membership) {
      // Store the membership ID for regular users
      localStorage.setItem(STORAGE_KEY_MEMBERSHIP, membership.id);
      // Also store full membership for super admins who might be viewing a league they're not in
      localStorage.setItem(STORAGE_KEY_SUPER_ADMIN, JSON.stringify(membership));
    } else {
      localStorage.removeItem(STORAGE_KEY_MEMBERSHIP);
      localStorage.removeItem(STORAGE_KEY_SUPER_ADMIN);
    }
  }, []);

  // Restore membership from localStorage
  const restoreMembershipFromStorage = useCallback((loadedMemberships: LeagueMembership[], userIsSuperAdmin: boolean) => {
    // First, try to restore from regular membership ID
    const storedMembershipId = localStorage.getItem(STORAGE_KEY_MEMBERSHIP);
    if (storedMembershipId && loadedMemberships.length > 0) {
      const found = loadedMemberships.find(m => m.id === storedMembershipId);
      if (found) {
        setCurrentMembershipState(found);
        return true;
      }
    }

    // For super admins, try to restore the full membership object (they might be viewing a league they're not in)
    if (userIsSuperAdmin) {
      const storedSuperAdmin = localStorage.getItem(STORAGE_KEY_SUPER_ADMIN);
      if (storedSuperAdmin) {
        try {
          const parsed = JSON.parse(storedSuperAdmin) as LeagueMembership;
          if (parsed && parsed.league_id && parsed.league) {
            setCurrentMembershipState(parsed);
            return true;
          }
        } catch (e) {
          console.error('Failed to parse stored super admin membership:', e);
        }
      }
    }

    // Fallback: auto-select first membership if available
    if (loadedMemberships.length > 0) {
      setCurrentMembership(loadedMemberships[0]);
      return true;
    }

    return false;
  }, [setCurrentMembership]);

  // Track if we're loading memberships to prevent duplicate calls
  const loadingMembershipsRef = useRef(false);
  const lastLoadedUserIdRef = useRef<string | null>(null);

  // Load memberships for user
  const loadMemberships = useCallback(async (userId: string, userEmail: string) => {
    console.log('📋 loadMemberships called for user:', userId);
    
    // Prevent duplicate simultaneous calls
    if (loadingMembershipsRef.current) {
      console.log('⏸️ Already loading memberships, skipping');
      return membershipsRef.current;
    }

    // If we already loaded for this user, skip (but still check super admin if not checked)
    if (lastLoadedUserIdRef.current === userId) {
      console.log('✅ Memberships already loaded for this user');
      // Still check super admin status if we haven't checked it yet
      if (lastCheckedUserIdRef.current !== userId) {
        console.log('🔍 Memberships cached, but checking super admin status...');
        await checkSuperAdminStatus(userId, userEmail);
      } else {
        console.log('✅ Memberships and super admin already cached');
      }
      return membershipsRef.current;
    }

    console.log('🔄 Loading fresh memberships and checking super admin...');

    loadingMembershipsRef.current = true;
    lastLoadedUserIdRef.current = userId;
    setLoadingMemberships(true);
    setMembershipsError(null); // Clear any previous errors

    try {
      // Check super admin status first (pass userId and email to avoid race conditions)
      const userIsSuperAdmin = await checkSuperAdminStatus(userId, userEmail);
      
      const { data, error } = await supabase
        .from('league_memberships')
        .select(`
          id,
          league_id,
          display_name,
          team_name,
          avatar_url,
          role,
          status,
          welcomed_at,
          league:leagues (id, name, slug, season)
        `)
        .eq('user_id', userId)
        .eq('status', 'active');

      if (error) {
        console.error('Error loading memberships:', error);
        // Extract status code from error
        const status = extractStatusCode(error);
        setMembershipsErrorStatus(status);
        // Treat error as connectivity issue, not "no leagues"
        setMembershipsError(error as Error);
        // Don't set memberships to empty array - keep previous state if any
        // Even on error, try to restore super admin membership from storage
        if (userIsSuperAdmin) {
          restoreMembershipFromStorage([], userIsSuperAdmin);
        }
        return [];
      } else if (data) {
        // Success - clear any previous errors
        setMembershipsError(null);
        setMembershipsErrorStatus(null);
        const transformed = data.map((m: LeagueMembership & { league: { id: string; name: string; slug: string } }) => ({
          ...m,
          league: m.league as { id: string; name: string; slug: string },
        }));
        setMemberships(transformed);

        // Try to restore from localStorage, otherwise auto-select first
        restoreMembershipFromStorage(transformed, userIsSuperAdmin);
        return transformed;
      } else {
        // No error, but no data either - this means user truly has no memberships
        setMembershipsError(null);
        setMembershipsErrorStatus(null);
        setMemberships([]);
        // Try to restore super admin membership from storage
        if (userIsSuperAdmin) {
          restoreMembershipFromStorage([], userIsSuperAdmin);
        }
        return [];
      }
    } catch (error) {
      console.error('Exception loading memberships:', error);
      // Extract status code from error
      const status = extractStatusCode(error);
      setMembershipsErrorStatus(status);
      // Treat exception as connectivity issue
      setMembershipsError(error as Error);
      // Don't set memberships to empty array - keep previous state if any
      return [];
    } finally {
      loadingMembershipsRef.current = false;
      setLoadingMemberships(false);
    }
  }, [restoreMembershipFromStorage, checkSuperAdminStatus]); // Keep checkSuperAdminStatus here since loadMemberships needs it

  // Refresh memberships (e.g., after accepting an invite)
  const refreshMemberships = useCallback(async (): Promise<LeagueMembership[]> => {
    if (user) {
      // Clear the cache so loadMemberships actually reloads
      lastLoadedUserIdRef.current = null;
      return await loadMemberships(user.id, user.email || '') || [];
    }
    return [];
  }, [user, loadMemberships]);

  // Store loadMemberships in a ref to avoid dependency issues
  const loadMembershipsRef = useRef(loadMemberships);
  loadMembershipsRef.current = loadMemberships;

  // Track activity: fire-and-forget update_last_active whenever currentMembership changes
  useEffect(() => {
    if (!currentMembership) return;
    supabase.rpc('update_last_active', { p_membership_id: currentMembership.id }).then(({ error }) => {
      if (error) console.warn('Failed to update last_active_at:', error.message);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership?.id]);

  // Initialize auth
  useEffect(() => {
    let isMounted = true;
    
    // Get initial session
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // loadMemberships already calls checkSuperAdminStatus, so no need to call it separately
          await loadMembershipsRef.current(session.user.id, session.user.email || '');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    initAuth();

    // Listen for auth changes (not async to avoid race conditions)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      
      const previousUserId = lastCheckedUserIdRef.current;
      const currentUserId = session?.user?.id;
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Only load if user changed or we haven't loaded yet
        if (previousUserId !== currentUserId || lastLoadedUserIdRef.current !== currentUserId) {
          // Clear cache if user changed
          if (previousUserId && previousUserId !== currentUserId) {
            lastCheckedUserIdRef.current = null;
            lastLoadedUserIdRef.current = null; // Also clear memberships cache on user change
          }
          // Fire and forget - don't await to avoid blocking
          // Use ref to avoid dependency on loadMemberships changing
          loadMembershipsRef.current(session.user.id, session.user.email || '');
        }
      } else {
        setMemberships([]);
        setMembershipsError(null);
        setMembershipsErrorStatus(null);
        setSuperAdminErrorStatus(null);
        setCurrentMembershipState(null);
        setIsSuperAdmin(false);
        lastCheckedUserIdRef.current = null; // Clear cache on logout
        lastLoadedUserIdRef.current = null; // Clear memberships cache
        failureCountRef.current = 0; // Reset failure count
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []); // Empty deps - use refs to access latest functions

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        // Network errors often show as "Failed to fetch"
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          return { 
            error: new Error('Network error: Unable to connect to authentication service. Please check your connection and try again.') 
          };
        }
        return { error: error as Error };
      }
      return { error: null };
    } catch (err) {
      // Catch any unexpected errors
      const error = err instanceof Error ? err : new Error('An unexpected error occurred during login');
      console.error('Login error:', error);
      return { error };
    }
  };

  const signInWithMagicLink = async (email: string, redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectTo || `${window.location.origin}/dashboard`,
      },
    });
    return { error: error as Error | null };
  };

  const signInWithPasskey = async (tokenHash: string, _email: string) => {
    try {
      // Use the verifyOtp method with the token_hash from the passkey auth
      // Note: token_hash (with underscore) is different from token - it's the hashed version
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'magiclink',
      });
      return { error: error as Error | null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unexpected error occurred during passkey sign-in');
      console.error('Passkey sign-in error:', error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string, name: string, redirectTo?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
        emailRedirectTo: redirectTo || `${window.location.origin}/dashboard`,
      },
    });
    return { error: error as Error | null, session: data?.session ?? null };
  };

  const signUpWithOtp = async (email: string, name: string, redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        data: { name },
        emailRedirectTo: redirectTo || `${window.location.origin}/dashboard`,
      },
    });
    return { error: error as Error | null };
  };

  const verifyOtp = async (email: string, token: string, type: 'email' | 'signup' = 'email') => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type,
    });
    return { error: error as Error | null };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setMemberships([]);
    setMembershipsError(null);
    setMembershipsErrorStatus(null);
    setSuperAdminErrorStatus(null);
    setCurrentMembershipState(null);
    setIsSuperAdmin(false);
    lastCheckedUserIdRef.current = null; // Clear cache on sign out
    lastLoadedUserIdRef.current = null; // Clear memberships cache
    failureCountRef.current = 0; // Reset failure count
    // Clear any stored membership preferences
    localStorage.removeItem(STORAGE_KEY_MEMBERSHIP);
    localStorage.removeItem(STORAGE_KEY_SUPER_ADMIN);
  };

  return (
    <AuthContext.Provider value={{
    user,
    session,
    loading,
    isSuperAdmin,
    checkingSuperAdmin,
    memberships,
    membershipsError,
    membershipsErrorStatus,
    loadingMemberships,
    superAdminErrorStatus,
    currentMembership,
    setCurrentMembership,
    refreshMemberships,
    signIn,
    signInWithMagicLink,
    signInWithPasskey,
    signUp,
    signUpWithOtp,
    verifyOtp,
    signOut,
    resetPassword,
    updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper hook for current user display info
export const useCurrentUser = () => {
  const { user, currentMembership, isSuperAdmin } = useAuth();
  
  if (!user) return null;
  
  return {
    id: user.id,
    email: user.email ?? '',
    name: user.user_metadata?.name ?? user.email ?? 'Unknown',
    displayName: currentMembership?.display_name ?? user.user_metadata?.name ?? user.email?.split('@')[0] ?? 'Unknown',
    teamName: currentMembership?.team_name ?? null,
    role: currentMembership?.role ?? (isSuperAdmin ? 'super_admin' : null),
    leagueId: currentMembership?.league_id ?? null,
    leagueName: currentMembership?.league?.name ?? null,
    isSuperAdmin,
  };
};
